import {
  useCallback,
  useEffect,
  useRef,
  type MouseEventHandler,
  type PointerEventHandler,
} from "react";

type UseLongPressOptions = {
  delay?: number;
  movementTolerance?: number;
  disabled?: boolean;
};

type LongPressHandlers<T extends HTMLElement> = {
  onPointerDown: PointerEventHandler<T>;
  onPointerMove: PointerEventHandler<T>;
  onPointerUp: PointerEventHandler<T>;
  onPointerCancel: PointerEventHandler<T>;
  onPointerLeave: PointerEventHandler<T>;
  // onClickCapture: MouseEventHandler<T>;
  onContextMenu: MouseEventHandler<T>;
};

const DEFAULT_LONG_PRESS_DELAY = 400;
const DEFAULT_MOVEMENT_TOLERANCE = 10;

function useLongPress<T extends HTMLElement>(
  onLongPress: () => void,
  {
    delay = DEFAULT_LONG_PRESS_DELAY,
    movementTolerance = DEFAULT_MOVEMENT_TOLERANCE,
    disabled = false,
  }: UseLongPressOptions = {},
): LongPressHandlers<T> {
  const callbackRef = useRef(onLongPress);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onLongPress;
  }, [onLongPress]);

  const clearLongPressTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearLongPressTimer, [clearLongPressTimer]);

  const resetPress = useCallback(() => {
    clearLongPressTimer();
    startPositionRef.current = null;
    pointerIdRef.current = null;
    longPressTriggeredRef.current = false;
  }, [clearLongPressTimer]);

  const onPointerDown = useCallback<PointerEventHandler<T>>(
    (event) => {
      if (disabled) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      clearLongPressTimer();
      longPressTriggeredRef.current = false;
      pointerIdRef.current = event.pointerId;
      startPositionRef.current = { x: event.clientX, y: event.clientY };

      timeoutRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        suppressClickRef.current = true;
        callbackRef.current();
      }, delay);
    },
    [clearLongPressTimer, delay, disabled],
  );

  const onPointerMove = useCallback<PointerEventHandler<T>>(
    (event) => {
      if (
        disabled ||
        pointerIdRef.current !== event.pointerId ||
        !startPositionRef.current ||
        longPressTriggeredRef.current
      ) {
        return;
      }

      const deltaX = Math.abs(event.clientX - startPositionRef.current.x);
      const deltaY = Math.abs(event.clientY - startPositionRef.current.y);

      if (deltaX > movementTolerance || deltaY > movementTolerance) {
        resetPress();
      }
    },
    [disabled, movementTolerance, resetPress],
  );

  const onPointerUp = useCallback<PointerEventHandler<T>>(
    () => {
      resetPress();
    },
    [resetPress],
  );

  const onPointerCancel = useCallback<PointerEventHandler<T>>(
    () => {
      resetPress();
    },
    [resetPress],
  );

  // const onClickCapture = useCallback<MouseEventHandler<T>>((event) => {
  //   if (!suppressClickRef.current) return;

  //   event.preventDefault();
  //   event.stopPropagation();
  //   suppressClickRef.current = false;
  // }, []);

  const onContextMenu = useCallback<MouseEventHandler<T>>((event) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave: onPointerCancel,
    // onClickCapture,
    onContextMenu,
  };
}

export default useLongPress;
